import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useBlogs from "hooks/useBlogs";
import { isReservedSlug } from "constants/reservedSlugs";
import Blog from "./Blog";
import { FadeInSection } from "components/general/FadeInSection";
import HomeGetStarted from "./HomeGetStarted";
import { useLenis } from "../../App";

function renderInline(text) {
  const pattern = /(\*\*[^*]+\*\*)|(\[[^\]]+\]\([^)]+\))|(\\[[\]])/g;
  const parts = [];
  let lastIndex = 0;
  let key = 0;
  let match;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    if (match[1]) {
      parts.push(<strong key={key++}>{match[1].slice(2, -2)}</strong>);
    } else if (match[2]) {
      const linkMatch = match[2].match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      parts.push(
        <a
          key={key++}
          href={linkMatch[2]}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#7dd88f", textDecoration: "underline" }}
        >
          {linkMatch[1]}
        </a>
      );
    } else if (match[3]) {
      parts.push(match[3][1]);
    }
    lastIndex = pattern.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
}

function stripHtmlWrapper(md) {
  return md.replace(/<\/?(p|div|span|br)\b[^>]*>/gi, (tag) =>
    /^<br/i.test(tag) ? "\n" : ""
  );
}

function renderMarkdown(md) {
  const source = stripHtmlWrapper(md);
  return source
    .split("\n\n")
    .filter((block) => block.trim())
    .map((block, i) => {
      const trimmed = block.trim();
      if (/^(\*\s*){3,}$|^(-\s*){3,}$|^(_\s*){3,}$/.test(trimmed)) {
        return (
          <hr
            key={i}
            style={{
              border: 0,
              borderTop: "1px solid rgba(255,255,255,0.2)",
              margin: "32px 0",
            }}
          />
        );
      }
      if (trimmed.startsWith("### ")) {
        return (
          <h3
            key={i}
            style={{
              fontSize: "1.25rem",
              fontWeight: 600,
              margin: "32px 0 8px",
              color: "white",
            }}
          >
            {renderInline(trimmed.slice(4))}
          </h3>
        );
      }
      if (trimmed.startsWith("## ")) {
        return (
          <h2
            key={i}
            className="title-cool"
            style={{
              fontSize: "1.6rem",
              margin: "0 0 16px",
              color: "white",
            }}
          >
            {renderInline(trimmed.slice(3))}
          </h2>
        );
      }
      const lines = trimmed.split("\n");
      if (lines.every((l) => /^- /.test(l.trim()))) {
        return (
          <ul
            key={i}
            style={{
              paddingLeft: "1.5rem",
              margin: "0 0 12px",
              color: "rgba(255,255,255,0.85)",
            }}
          >
            {lines.map((l, j) => (
              <li key={j} style={{ lineHeight: 1.7, marginBottom: 4 }}>
                {renderInline(l.trim().slice(2))}
              </li>
            ))}
          </ul>
        );
      }
      return (
        <p
          key={i}
          style={{
            fontSize: "1rem",
            lineHeight: 1.7,
            color: "rgba(255,255,255,0.85)",
            margin: "0 0 12px",
          }}
        >
          {renderInline(trimmed)}
        </p>
      );
    });
}

export default function BlogPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const lenis = useLenis();
  const { blogs, loading, error } = useBlogs();
  const slugIsReserved = isReservedSlug(slug);
  const post = slugIsReserved
    ? null
    : blogs.find((p) => p.slug === slug) || blogs.find((p) => p.id === slug);
  console.log("api/v1/public/blogs", blogs);

  useEffect(() => {
    if (lenis) {
      lenis.scrollTo(0, { immediate: true });
    }
  }, [lenis]);

  if (loading && !slugIsReserved) {
    return (
      <div
        style={{
          margin: 32,
          padding: 32,
          borderRadius: 32,
          background: "linear-gradient(180deg, #00140A 0%, #014922 100%)",
          color: "white",
          textAlign: "center",
        }}
      >
        <p style={{ opacity: 0.6 }}>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          margin: 32,
          padding: 32,
          borderRadius: 32,
          background: "linear-gradient(180deg, #00140A 0%, #014922 100%)",
          color: "white",
          textAlign: "center",
        }}
      >
        <h2
          className="title-cool"
          style={{ fontSize: "2rem", marginBottom: 16 }}
        >
          {error}
        </h2>
        <button
          className="primary-button outline-button"
          onClick={() => navigate("/")}
        >
          Back to home
        </button>
      </div>
    );
  }

  if (!post) {
    return (
      <div
        style={{
          margin: 32,
          padding: 32,
          borderRadius: 32,
          background: "linear-gradient(180deg, #00140A 0%, #014922 100%)",
          color: "white",
          textAlign: "center",
        }}
      >
        <h2
          className="title-cool"
          style={{ fontSize: "2rem", marginBottom: 16 }}
        >
          Post not found
        </h2>
        <button
          className="primary-button outline-button"
          onClick={() => navigate("/")}
        >
          Back to home
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        margin: 32,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      {/* Blog */}
      <div
        style={{
          padding: 32,
          borderRadius: 32,
          background: "linear-gradient(180deg, #00140A 0%, #014922 100%)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <img
          src={post.image}
          alt={post.title}
          style={{
            width: "100%",
            maxWidth: 931,
            borderRadius: 16,
            objectFit: "cover",
            aspectRatio: "16 / 9",
          }}
        />

        <span
          style={{
            fontSize: 13,
            color: "rgba(255,255,255,0.6)",
            letterSpacing: "0.08em",
            marginTop: 24,
          }}
        >
          {post.date} · By {post.author ?? "NumbersAG"}
        </span>

        {post.section && (
          <span className="blog-section-badge">{post.section}</span>
        )}

        <h1
          className="title-cool"
          style={{
            fontSize: "clamp(1.6rem, 4vw, 2.5rem)",
            color: "white",
            textAlign: "center",
            margin: "12px 0 32px",
            maxWidth: 931,
          }}
        >
          {post.title}
        </h1>

        <div style={{ maxWidth: 931, width: "100%" }}>
          {renderMarkdown(post.content)}
          {post.keywords && (
            <div className="blog-tags">
              {post.keywords
                .split(",")
                .map((k) => k.trim())
                .filter(Boolean)
                .map((tag) => (
                  <span key={tag} className="blog-tag">
                    #{tag}
                  </span>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* Blogs */}
      <FadeInSection>
        <Blog />
      </FadeInSection>

      <FadeInSection>
        <HomeGetStarted />
      </FadeInSection>
    </div>
  );
}
